import { Controller } from "@hotwired/stimulus"

// Manages column order + visibility for Cup::DataTable::Component.
//
// - Drag-and-drop reorder directly on the <th>s.
// - Hide/show via a settings popover (popup/popupList targets, rendered by
//   Cup::DataTable::ColumnSettings) with a checkbox per movable column.
// - "Locked" columns (data-column-locked="true": selection, indicator,
//   settings) are excluded from drag, reorder, hide and the popup list.
// - Persisted in a cookie named after `storageKeyValue` (e.g. cup_cols_users).
//
// DOM contract (set by Cup::DataTable::Component, T9):
//
//   <table data-controller="columns scroll-memory"
//          data-columns-storage-key-value="cup_cols_<dom_id>">
//     <thead><tr>
//       <th data-columns-target="header" data-column-id="name">…</th>
//       <th data-columns-target="header" data-column-id="email">…</th>
//     </tr></thead>
//     <tbody data-columns-target="body">
//       <tr>
//         <td data-column-id="name">…</td>
//         <td data-column-id="email">…</td>
//       </tr>
//     </tbody>
//   </table>
//
// Ported from Werner's columns_controller.js (see his MySQL branch) with
// the following adaptations:
//   - target names: header/body (was: table/thead)
//   - cell attribute: data-column-id (was: data-column)
export default class extends Controller {
  static targets = ["header", "body", "popup", "popupList", "emptyCell"]
  static values  = { storageKey: String, defaultOrder: Array }

  connect() {
    if (!this.hasHeaderTarget) return
    this.headerRow = this.headerTargets[0].parentElement // the <tr> inside <thead>
    if (!this.headerRow) return

    // Build column metadata from the current (= default) DOM order. Locked
    // columns (selection / indicator / settings) are excluded from reorder,
    // hide and the settings popup — they keep their fixed position.
    this.columns = this.headerTargets.map(th => ({
      id:            th.dataset.columnId,
      locked:        th.dataset.columnLocked === "true",
      defaultHidden: th.dataset.columnDefaultHidden === "true",
      label:         this.thLabel(th)
    }))

    this.applyState(this.readState())
    this.buildPopupList()
    this.bindDragHandlers()
    this.boundDocClick = this.onDocClick.bind(this)
    document.addEventListener("click", this.boundDocClick)
  }

  disconnect() {
    if (this.boundDocClick) document.removeEventListener("click", this.boundDocClick)
  }

  columnById(id) {
    return this.columns.find(c => c.id === id)
  }

  // ---------- state ----------

  cookieName() {
    return this.storageKeyValue || "cup_cols_default"
  }

  // Visibility has two independent axes so per-column `defaultHidden` survives a
  // saved (or corrupt/legacy) cookie: `hidden` lists ordinary columns the user
  // hid; `shown` lists default-hidden columns the user explicitly revealed. A
  // cookie that predates a column's defaultHidden flag simply lacks it from
  // `shown`, so the column stays hidden — the intended default.
  readState() {
    const blank = { order: [], hidden: [], shown: [] }
    const raw = this.readCookie(this.cookieName())
    if (!raw) return blank
    try {
      const parsed = JSON.parse(raw)
      return {
        order:  Array.isArray(parsed.order)  ? parsed.order  : [],
        hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
        shown:  Array.isArray(parsed.shown)  ? parsed.shown  : []
      }
    } catch (_e) {
      return blank
    }
  }

  writeState(state) {
    const value = encodeURIComponent(JSON.stringify(state))
    const thirtyDays = 60 * 60 * 24 * 30
    document.cookie = `${this.cookieName()}=${value}; path=/; max-age=${thirtyDays}; samesite=lax`
  }

  currentState() {
    // When defaultOrder is declared the order is reset on every load and never
    // persisted — only visibility (hidden/shown) is remembered.
    const order  = this.defaultOrderValue?.length > 0 ? [] : this.movableColumnIds()
    const hidden = this.columns.filter(c => !c.locked && !c.defaultHidden && this.isHidden(c.id)).map(c => c.id)
    const shown  = this.columns.filter(c => !c.locked && c.defaultHidden && !this.isHidden(c.id)).map(c => c.id)
    return { order, hidden, shown }
  }

  persist() {
    this.writeState(this.currentState())
  }

  reset() {
    document.cookie = `${this.cookieName()}=; path=/; max-age=0`
    window.location.reload()
  }

  // Public action: toggle visibility of a column by id (skips locked columns).
  toggleHidden(id) {
    if (this.columnById(id)?.locked) return
    this.setHidden(id, !this.isHidden(id))
    this.adjustEmptyCell()
    this.persist()
  }

  // ---------- apply to DOM ----------

  applyState(state) {
    // Reorder only movable (non-locked) columns; locked columns keep their
    // original position.
    const knownIds = new Set(this.columns.map(c => c.id))
    const domOrder = this.columns.filter(c => !c.locked).map(c => c.id)
    const explicit = (this.defaultOrderValue || [])
      .filter(id => knownIds.has(id) && !this.columnById(id)?.locked)
    const movableDefault = explicit.length > 0
      ? [...explicit, ...domOrder.filter(id => !explicit.includes(id))]
      : domOrder
    // When defaultOrder is declared it is authoritative on every load — ignore
    // any saved cookie order so the page always opens in the declared order.
    const stateOrder = explicit.length > 0
      ? []
      : state.order.filter(id => knownIds.has(id) && !this.columnById(id).locked)
    const missing    = movableDefault.filter(id => !stateOrder.includes(id))
    const targetMovableOrder = [...stateOrder, ...missing]

    this.reorderMovables(targetMovableOrder)

    const hiddenIds = new Set(state.hidden.filter(id => knownIds.has(id) && !this.columnById(id).locked))
    const shownIds  = new Set(state.shown.filter(id => knownIds.has(id) && !this.columnById(id).locked))
    this.columns.forEach(c => {
      if (c.locked) return
      const hide = c.defaultHidden ? !shownIds.has(c.id) : hiddenIds.has(c.id)
      this.setHidden(c.id, hide)
    })
    this.adjustEmptyCell()
  }

  reorderMovables(targetMovableOrder) {
    // Apply the same reorder to the header row + every body row so columns
    // line up vertically. Locked columns stay put on their movable positions.
    const rows = [this.headerRow, ...this.bodyRows()]
    rows.forEach(row => this.reorderRowMovables(row, targetMovableOrder))
  }

  reorderRowMovables(row, targetMovableOrder) {
    const cells = Array.from(row.querySelectorAll(":scope > th[data-column-id], :scope > td[data-column-id]"))
    if (cells.length === 0) return

    const isMovable = (cell) =>
      cell.dataset.columnLocked !== "true" && !this.columnById(cell.dataset.columnId)?.locked

    // The DOM nodes currently sitting on each movable slot, in order. We insert
    // each target movable cell before the node that originally followed that
    // slot, so locked columns (selection / indicator / settings) stay put.
    const movableSlots = cells.filter(isMovable)
    const movableByCol = {}
    movableSlots.forEach(cell => { movableByCol[cell.dataset.columnId] = cell })

    targetMovableOrder.forEach((id, i) => {
      const cell      = movableByCol[id]
      const slotNode  = movableSlots[i]
      if (cell && slotNode && cell !== slotNode) {
        row.insertBefore(cell, slotNode)
      }
    })
  }

  setHidden(colId, hide) {
    const cells = this.element.querySelectorAll(`[data-column-id="${colId}"]`)
    cells.forEach(cell => {
      if (hide) cell.style.display = "none"
      else      cell.style.removeProperty("display")
    })
  }

  // The empty-state row is a single colspan cell with no per-column cells to
  // hide, so its server-rendered colspan can exceed the number of visible
  // columns once data columns are hidden. In a table-fixed layout an oversized
  // colspan redistributes the grid and shifts the headers — keep it equal to
  // the count of visible header cells (locked checkbox/settings included).
  adjustEmptyCell() {
    if (!this.hasEmptyCellTarget || !this.headerRow) return
    const visible = Array.from(this.headerRow.children)
      .filter(th => th.style.display !== "none").length
    this.emptyCellTarget.colSpan = visible
  }

  isHidden(colId) {
    const th = this.headerRow.querySelector(`th[data-column-id="${colId}"]`)
    return th ? th.style.display === "none" : false
  }

  movableColumnIds() {
    return Array.from(this.headerRow.querySelectorAll("th[data-column-id]"))
      .filter(th => th.dataset.columnLocked !== "true")
      .map(th => th.dataset.columnId)
  }

  bodyRows() {
    if (!this.hasBodyTarget) return []
    return Array.from(this.bodyTarget.querySelectorAll(":scope > tr"))
  }

  thLabel(th) {
    const explicit = th.dataset.columnLabel
    if (explicit) return explicit
    const text = th.textContent.trim().replace(/[↕↑↓]/g, "").trim()
    return text || th.dataset.columnId
  }

  // ---------- settings popup ----------

  togglePopup(e) {
    e.preventDefault()
    e.stopPropagation()
    if (!this.hasPopupTarget) return
    this.popupTarget.classList.toggle("hidden")
  }

  hidePopup() {
    if (this.hasPopupTarget) this.popupTarget.classList.add("hidden")
  }

  onDocClick(e) {
    if (!this.hasPopupTarget) return
    if (this.popupTarget.classList.contains("hidden")) return
    if (this.popupTarget.contains(e.target)) return
    if (e.target.closest("[data-action*='columns#togglePopup']")) return
    this.hidePopup()
  }

  buildPopupList() {
    if (!this.hasPopupListTarget) return
    this.popupListTarget.innerHTML = ""
    this.columns.filter(c => !c.locked).forEach(col => {
      const label = document.createElement("label")
      label.className = "flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50 cursor-pointer dark:text-zinc-200 dark:hover:bg-zinc-800/60 whitespace-nowrap"
      const cb = document.createElement("input")
      cb.type = "checkbox"
      cb.checked = !this.isHidden(col.id)
      cb.className = "h-4 w-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500 dark:border-zinc-600 dark:bg-zinc-800"
      cb.addEventListener("change", () => {
        this.setHidden(col.id, !cb.checked)
        this.adjustEmptyCell()
        this.persist()
      })
      const span = document.createElement("span")
      span.textContent = col.label
      label.appendChild(cb)
      label.appendChild(span)
      this.popupListTarget.appendChild(label)
    })
  }

  resetAction(e) {
    e.preventDefault()
    this.reset()
  }

  // ---------- drag-and-drop ----------

  bindDragHandlers() {
    this.headerTargets.forEach(th => {
      if (th.dataset.columnLocked === "true") return
      th.setAttribute("draggable", "true")
      th.classList.add("cursor-move")
      th.addEventListener("dragstart", this.onDragStart.bind(this))
      th.addEventListener("dragover",  this.onDragOver.bind(this))
      th.addEventListener("dragleave", this.onDragLeave.bind(this))
      th.addEventListener("drop",      this.onDrop.bind(this))
      th.addEventListener("dragend",   this.onDragEnd.bind(this))
    })
  }

  onDragStart(e) {
    const th = e.currentTarget
    const colId = th.dataset.columnId
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", colId)
    this.dragSourceId = colId
    th.classList.add("opacity-40")
  }

  onDragOver(e) {
    if (!this.dragSourceId) return
    const th = e.currentTarget
    if (th.dataset.columnId === this.dragSourceId) return
    if (th.dataset.columnLocked === "true") return
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    th.classList.add("ring-2", "ring-orange-400", "ring-inset")
  }

  onDragLeave(e) {
    e.currentTarget.classList.remove("ring-2", "ring-orange-400", "ring-inset")
  }

  onDrop(e) {
    e.preventDefault()
    const target = e.currentTarget
    target.classList.remove("ring-2", "ring-orange-400", "ring-inset")
    const sourceId = this.dragSourceId
    const targetId = target.dataset.columnId
    if (!sourceId || !targetId || sourceId === targetId) return
    if (target.dataset.columnLocked === "true") return

    const order   = this.movableColumnIds()
    const fromIdx = order.indexOf(sourceId)
    const toIdx   = order.indexOf(targetId)
    if (fromIdx < 0 || toIdx < 0) return
    order.splice(fromIdx, 1)
    const insertAt = order.indexOf(targetId) + (fromIdx < toIdx ? 1 : 0)
    order.splice(insertAt, 0, sourceId)

    this.reorderMovables(order)
    this.persist()
  }

  onDragEnd(_e) {
    this.dragSourceId = null
    this.headerTargets.forEach(th => {
      th.classList.remove("opacity-40", "ring-2", "ring-orange-400", "ring-inset")
    })
  }

  // ---------- cookie helper ----------

  readCookie(name) {
    const match = document.cookie.split("; ").find(c => c.startsWith(name + "="))
    if (!match) return null
    try {
      return decodeURIComponent(match.split("=").slice(1).join("="))
    } catch (_e) {
      return null
    }
  }
}
